import React from 'react';
import { sentenceCase } from 'change-case';
import { Table, Descriptions, Skeleton, Row, Col, Tag } from 'antd';
import { Link, useRouteMatch } from 'react-router-dom';
import { ColumnsType } from 'antd/lib/table';
import { Stack } from '../../types/Stack';
import { StackedJob } from '../../types/Job';
import { Pagination } from '../../utils/PaginationHandler';

interface StackListInterfaceProps {
    wrapperStyleClass?: string;
    stacks: Stack[];
    pagination: Pagination;
    isLoadingData: boolean;
    onPageChanges(filters: any, opts?: { isPaging: boolean }): void;
}

const StackList: React.FC<StackListInterfaceProps> = (props: StackListInterfaceProps): React.ReactElement => {
    const { wrapperStyleClass, stacks, pagination, onPageChanges: setFiltersToApply, isLoadingData } = props;

    const routeMatch = useRouteMatch();

    const renderInnerRowComponent = (job: StackedJob): JSX.Element => {
        const propsToRender: { key: string; value: any }[] = [
            { key: 'createdAt', value: job.createdAt && new Date(job.createdAt).toLocaleString() },
            { key: 'clientReference', value: job.deliveries[0].dropoff.clientReference },
            { key: 'pickupAt', value: job.pickupAt && new Date(job.pickupAt).toLocaleString() },
            { key: 'dropoffAt', value: job.dropoffAt && new Date(job.dropoffAt).toLocaleString() },
        ];
        return (
            <Row gutter={24} className="p-3 mb-3 rounded bg-white shadow-sm justify-content-between">
                <Col>
                    <p className="m-0">
                        <span className="d-block w-100">
                            <strong>Job Id:</strong>
                        </span>
                        <span className="d-block w-100">
                            <Link
                                to={`/admin/jobs/detail/${job.id}`}
                                title="View detail"
                                id={`job-detail-link-${job.id}`}
                            >
                                <Tag color="#2db7f5" style={{ cursor: 'pointer' }}>
                                    {job.id}
                                </Tag>
                            </Link>
                        </span>
                    </p>
                </Col>
                {propsToRender.map((prop) => (
                    <Col key={prop.key}>
                        <p className="m-0">
                            <span className="d-block w-100 text-capitalize">
                                <strong>{`${sentenceCase(prop.key)}:`}</strong>
                            </span>
                            <span className="d-block w-100">{prop.value || '-'}</span>
                        </p>
                    </Col>
                ))}
            </Row>
        );
    };

    const columns: ColumnsType<Stack> = [
        {
            title: 'Middleware Id',
            dataIndex: 'id',
            key: 'id',
            width: '15%',
            responsive: ['xs', 'sm', 'md'],
            render(id: number): JSX.Element {
                return (
                    <Link to={`${routeMatch.path}/detail/${id}`} id="stack-list__btn-view-detail" title="View detail">
                        {id}
                    </Link>
                );
            },
        },
        {
            title: 'API Job Id',
            dataIndex: 'apiJobId',
            key: 'apiJobId',
            align: 'center',
            width: '15%',
            responsive: ['md'],
        },
        {
            title: 'Created at',
            dataIndex: 'createdAt',
            key: 'createdAt',
            align: 'center',
            width: '20%',
            responsive: ['md'],
            render(createdAt: Date | string): string {
                return `${new Date(createdAt).toLocaleString()}`;
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            align: 'center',
            width: '30%',
            responsive: ['xs', 'sm', 'md'],
            render(status: string): string {
                return `${sentenceCase(status)}`;
            },
        },

        {
            title: 'Client',
            dataIndex: 'client',
            key: 'client',
            align: 'center',
            width: '20%',
            responsive: ['md'],
            render(client: { id: number; name: string }): JSX.Element {
                return <span>{client.name}</span>;
            },
        },
    ];

    if (isLoadingData) {
        return <Skeleton active className="p-4" />;
    }

    return (
        <div className={wrapperStyleClass || 'px-4 pb-4 bg-white'}>
            <Descriptions size="small">
                <Descriptions.Item label={<span className="text-black-50">N&deg; stacked jobs</span>}>
                    <span className="text-black-50">{pagination.totalItems}</span>
                </Descriptions.Item>
            </Descriptions>
            <Table
                columns={columns}
                dataSource={stacks}
                tableLayout="fixed"
                expandable={{
                    // eslint-disable-next-line react/display-name
                    expandedRowRender: (record: Stack): JSX.Element => {
                        return (
                            <Row gutter={24}>
                                {record.jobs.map((job) => (
                                    <Col key={job.id} span={24} className="px-4 bg-transparent">
                                        {renderInnerRowComponent(job)}
                                    </Col>
                                ))}
                            </Row>
                        );
                    },
                    rowExpandable: (record): boolean => record.jobs.length > 0,
                }}
                pagination={{
                    pageSize: pagination.itemsPerPage,
                    current: pagination.pageNumber,
                    total: pagination.totalItems,
                    showSizeChanger: false,
                    itemRender: (current, type, originalElement): string =>
                        pagination.uiControlsRenderer(current, type, originalElement),
                    onChange: (page: number, pageSize?: number): void =>
                        setFiltersToApply(pagination.getPagingFilters(page, pageSize), { isPaging: true }),
                }}
            />
        </div>
    );
};

export default StackList;
